import { useCallback, useEffect, useMemo, useRef } from 'react';
import ReactFlow, { Background, Controls, Handle, Position } from 'react-flow-renderer';
import { Check, Loader2, Circle, Ban, X, Minus } from 'lucide-react';

/**
 * Renders the workflow task graph (e.g. New User Provisioning steps) as a left-to-right
 * node/edge graph. The current ("active") step pulses and its incoming edge animates so the
 * user can see which task the agent is on. Purely presentational — steps come from the
 * ticket's workflow_state.
 */

export const STATUS_STYLES = {
  done: 'bg-cp-ok text-white border-cp-ok',
  active: 'bg-cp-grad text-white border-cp-violet ring-4 ring-cp-violet/30 animate-pulse',
  pending: 'bg-cp-surface text-cp-muted border-cp-line',
  skipped: 'bg-cp-surface2 text-cp-faint border-cp-line line-through',
  failed: 'bg-cp-crit text-white border-cp-crit',
  rejected: 'bg-cp-inset text-cp-mod border-cp-mod',
  waived: 'bg-cp-surface2 text-cp-muted border-cp-line border-dashed',
};

const STATUS_WORDS = {
  failed: 'Failed',
  rejected: 'Rejected',
  waived: 'Waived',
};

function StatusIcon({ status }) {
  if (status === 'done') return <Check className="h-4 w-4" />;
  if (status === 'active') return <Loader2 className="h-4 w-4 animate-spin" />;
  if (status === 'rejected') return <X className="h-4 w-4" />;
  if (status === 'waived') return <Minus className="h-4 w-4" />;
  if (status === 'skipped' || status === 'failed') return <Ban className="h-4 w-4" />;
  return <Circle className="h-4 w-4" />;
}

const StepNode = ({ data }) => {
  const status = data.status || 'pending';
  return (
    <div
      className={`px-4 py-3 rounded-xl shadow-md border-2 w-[210px] transition-all ${
        STATUS_STYLES[status] || STATUS_STYLES.pending
      }`}
    >
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} isConnectable={false} />
      <div className="flex items-center gap-2">
        <StatusIcon status={status} />
        <span className="text-[11px] font-semibold uppercase tracking-wide opacity-80">
          Step {data.index + 1}
        </span>
      </div>
      <div className="mt-1 text-sm font-medium leading-snug">{data.label}</div>
      {data.detail ? (
        <div className="mt-1 text-[10px] leading-snug opacity-80">{data.detail}</div>
      ) : null}
      {STATUS_WORDS[status] ? (
        <div className="mt-1 text-[10px] font-semibold uppercase tracking-wide">{STATUS_WORDS[status]}</div>
      ) : null}
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} isConnectable={false} />
    </div>
  );
};

const nodeTypes = { step: StepNode };

const NODE_GAP = 250;
const NODE_WIDTH = 210;
const FIT_ALL_MIN_ZOOM = 0.6;

/**
 * Keep the current milestone centered with its neighbours visible when the whole
 * graph would need to shrink below a readable zoom; otherwise fit everything.
 */
export function computeGraphViewport({ stepCount, activeIndex, containerWidth }) {
  const width = Math.max(Number(containerWidth) || 0, 1);
  const totalWidth = Math.max(stepCount, 1) * NODE_GAP - (NODE_GAP - NODE_WIDTH);
  const fitZoom = (width * 0.9) / totalWidth;
  if (fitZoom >= FIT_ALL_MIN_ZOOM || activeIndex < 0) {
    return { mode: 'fit' };
  }
  const zoom = Math.min(0.9, Math.max(0.55, width / (3 * NODE_GAP + 60)));
  return {
    mode: 'center',
    x: activeIndex * NODE_GAP + NODE_WIDTH / 2,
    y: 30,
    zoom,
  };
}

export default function WorkflowTaskGraph({ steps = [], currentStep, height = 240 }) {
  const wrapperRef = useRef(null);
  const instanceRef = useRef(null);
  const activeKey = currentStep || steps.find((s) => s.status === 'active')?.key || null;
  const activeIndex = steps.findIndex((s, i) => (s.key || `step-${i}`) === activeKey);

  const focusViewport = useCallback(() => {
    const instance = instanceRef.current;
    if (!instance) return;
    const containerWidth = wrapperRef.current?.getBoundingClientRect?.().width || 0;
    const viewport = computeGraphViewport({
      stepCount: steps.length,
      activeIndex,
      containerWidth,
    });
    if (viewport.mode === 'center' && typeof instance.setCenter === 'function') {
      instance.setCenter(viewport.x, viewport.y, { zoom: viewport.zoom, duration: 300 });
    } else if (typeof instance.fitView === 'function') {
      instance.fitView({ padding: 0.2 });
    }
  }, [steps.length, activeIndex]);

  useEffect(() => {
    focusViewport();
  }, [focusViewport]);

  const { nodes, edges } = useMemo(() => {
    const builtNodes = steps.map((step, index) => ({
      id: step.key || `step-${index}`,
      type: 'step',
      position: { x: index * 250, y: 0 },
      data: { label: step.label, detail: step.detail || '', status: step.status || 'pending', index },
      draggable: false,
      connectable: false,
      selectable: false,
    }));

    const builtEdges = steps.slice(1).map((step, index) => {
      const target = step.key || `step-${index + 1}`;
      const source = steps[index].key || `step-${index}`;
      const targetActive = (step.key && step.key === activeKey) || step.status === 'active';
      const bothDone = steps[index].status === 'done' && step.status === 'done';
      return {
        id: `${source}->${target}`,
        source,
        target,
        animated: targetActive,
        style: {
          stroke: targetActive ? '#f59e0b' : bothDone ? '#22c55e' : '#cbd5e1',
          strokeWidth: 2,
        },
      };
    });

    return { nodes: builtNodes, edges: builtEdges };
  }, [steps, activeKey]);

  return (
    <div
      ref={wrapperRef}
      style={{ height: typeof height === 'number' ? `${height}px` : height, width: '100%' }}
      data-testid="workflow-task-graph"
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.3}
        onInit={(instance) => {
          instanceRef.current = instance;
          focusViewport();
        }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        zoomOnScroll={false}
        panOnScroll={false}
        preventScrolling={false}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={16} color="#eef2f7" />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}
